import {Injectable} from '@angular/core';
import {CartAdvancedInterface} from './cart.interface';
import {
  BookingItemLight,
  Cart,
  CartService as Api,
  CartTotal,
  Checkout,
  CheckoutRequest,
  Discount,
  EventPricing,
  EventTicketIds,
  LegacyTicket,
  PaymentTypeEnum,
  PlacesRequest,
  PriceClass,
  PriceInfoEnum,
  PrintTicketType,
  PromocodeRequest,
  Response,
  Right,
  Sector,
  SelfServiceService as SelfServiceApi,
  SendTransaction,
  SendType,
  TicketBarcodes,
  TicketIds,
  TicketPrice,
  TicketPriceType,
  TicketsRequest,
  TicketsResponse,
  Transaction,
  UnassignedTicket
} from '../../generated/sales-terminal-client';
import {AuthService} from '../auth/auth.service';
import {LanguageService} from '../language/language.service';
import {SnackService} from '../snack/snack.service';
import {forkJoin, Observable, Subscription, tap} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {PriceInCentsPipe} from '../../pipes/priceInCents/priceInCents.pipe';
import {TicketPricePipe} from '../../pipes/ticketPrice/ticketPrice.pipe';
import {PricePipe} from '../../pipes/price/price.pipe';
import {StorageService} from '../storage/storage.service';
import {cloneDeep} from 'lodash-es';
import moment from 'moment';
import {EventService} from '../event/event.service';
import {CartBaseService, CartSortedEvent, CartType, CartTypes} from './cart-base.service';
import {CustomTicket} from '../../modules/sales-terminal/cart/table/cart-table.component';
import {WebshopCustomer} from '../../generated/customer-client';
import {EventPurchaseComponent} from '../../modules/sales-terminal/event-purchase/event-purchase.component';
import {RouteService} from '../route/route.service';
import {SelfServiceEventService} from '../self-service/event/self-service-event.service';
import {TranslateService} from '@ngx-translate/core';
import {MAX_TICKETS_IN_CART} from "../../modules/sales-terminal/sales-terminal.constants";
import {ViewService} from "../view/view.service";
import {CentrePropertyService} from "../centre-property/centre-property.service";

export interface SelectedPriceZone {
  priceZone: PriceClass;
  tickets: LegacyTicket[];
}
export interface SelectedSector {
  sector: Sector;
  tickets: LegacyTicket[];
  priceZones: SelectedPriceZone[];
}
export interface SelectedTicketsMap {
  event: CartSortedEvent;
  selectedTickets?: LegacyTicket[];
  selectedTicketTypes?: UnassignedTicket[];
  sectors?: SelectedSector[];
}
export interface SelectedEvent {
  id: number;
  boEvent: boolean;
}

@Injectable()
export class CartService extends CartBaseService implements CartAdvancedInterface<Cart> {
  public static STORAGE_CART_UPDATE = 'cart.update';
  public static STORAGE_CART_CLEAR = 'cart.clear';
  public static STORAGE_CART_CHECKOUT = 'cart.checkout';
  public static STORAGE_CART_PREVIEW = 'cart.preview';
  public static STORAGE_CART_PURCHASE_SUCCEED = 'cart.purchaseSucceed';
  public static STORAGE_CART_UPDATE_SUBTOTALS = 'cart.updateSubtotals';
  public static STORAGE_CART_PROMOCODE_REMOVED = 'cart.promocodeRemoved';
  public static STORAGE_CART_WINDOW_CLOSE = 'cart.windowClose';
  public static STORAGE_CART_LAST_BOOKING = 'cart.lastBooking';
  public static STORAGE_CART_LAST_EVENT_ID = 'cart.lastEventId';
  public static STORAGE_CART_PRESERVE_GIFTS = 'cart.preserveGifts';
  public static STORAGE_CART_CLIENT_NOTIFICATION_EMAIL = 'cart.clientNotificationEmail';
  public static STORAGE_CART_TYPE = 'cart.cartType';

  public webShopCustomer: WebshopCustomer;
  public preserveGiftTickets: boolean;
  public showSelectedTickets: boolean;
  public cartSubscription: Subscription;
  public selectedTicketsMap: SelectedTicketsMap = {
    event: null,
    selectedTickets: [],
    sectors: []
  };
  private _selectedTicketsTotal: CartTotal;
  private _selectedEvent: SelectedEvent;

  private _lastEventId: number;
  public get lastEventId(): number {
    return this._lastEventId;
  }
  public set lastEventId(eventId: number) {
    this._lastEventId = eventId;
    this.storageService.set(CartService.STORAGE_CART_LAST_EVENT_ID, eventId);
  }
  public eventPricing: EventPricing;
  public eventPricingMap: Record<number, EventPricing>;
  public timeRemaining: number;
  private _expiryInterval: any;

  constructor(private _api: Api,
              private _selfServiceApi: SelfServiceApi,
              private translateService: TranslateService,
              protected authService: AuthService,
              protected centrePropertyService: CentrePropertyService,
              protected languageService: LanguageService,
              protected snackService: SnackService,
              protected storageService: StorageService,
              protected eventService: EventService,
              protected selfServiceEventService: SelfServiceEventService,
              protected routeService: RouteService,
              protected priceInCentsPipe: PriceInCentsPipe,
              protected ticketPricePipe: TicketPricePipe,
              protected pricePipe: PricePipe,
              protected viewService: ViewService) {
    super(authService, centrePropertyService, languageService, snackService, storageService, eventService, selfServiceEventService, routeService, priceInCentsPipe, ticketPricePipe, pricePipe);
    if (storageService.get(CartService.STORAGE_CART_LAST_EVENT_ID)) {
      this._lastEventId = JSON.parse(storageService.get(CartService.STORAGE_CART_LAST_EVENT_ID));
    }
    if (storageService.get(CartService.STORAGE_CART_PRESERVE_GIFTS)) {
      this.preserveGiftTickets = JSON.parse(storageService.get(CartService.STORAGE_CART_PRESERVE_GIFTS));
    }
    if (storageService.get(CartService.STORAGE_CART_LAST_BOOKING)) {
      super.updateCurrentBooking(JSON.parse(storageService.get(CartService.STORAGE_CART_LAST_BOOKING)));
    }
    const storageCartType: string = storageService.get(CartService.STORAGE_CART_TYPE);
    if (storageCartType?.length && [CartTypes.SelfService, CartTypes.Legacy].includes(storageCartType as CartType)) {
      this.cartType = storageCartType as CartType;
    }
  }

  public override get cart(): Cart {
    return this._cart || this._emptyCart;
  }
  public override set cart(newCart: Cart) {
    this._cart = newCart;
    this.setCartTimer();
    this.cartTotal = newCart.total;
    if (!this.isSelfService && !this.preserveGiftTickets && !this._cart.events?.length && this.giftCodes?.length) {
      this.clearGiftTickets();
    }
    this.preserveGiftTickets = false;
    this.mapTickets(this.events);
    this.eventPricingMap = {};
    if (this.isSelfService && this.cartEventIds?.length) {
      this.updateEventPricings();
    }
    this.updateSelectionMap();
  }

  private updateEventPricings(): void {
    const requestList: Observable<EventPricing>[] = [];
    this.cartEventIds.forEach(eventId => {
      requestList.push(this.selfServiceEventService.getEventPricing(eventId));
    });
    forkJoin(requestList).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    ).subscribe((results: EventPricing[]) => {
      this.cartEventIds.forEach((eventId, idx) => {
        this.eventPricingMap[eventId] = results[idx];
      });
      this.nextMarkForCheck();
    });
  }

  public getCart(): Observable<Cart> {
    if (this.isSelfService || !this.authService.user.supportsYbo) {
      return this._selfServiceApi.selfServiceCartGet().pipe(
        tap({
          error: (e) => this.snackService.showBackendError(e),
          next: (cart) => this.setCartByType(cart)
        })
      );
    }
    return this._api.getCart().pipe(
      tap({
        error: (e) => this.snackService.showBackendError(e),
        next: (cart) => this.setCartByType(cart)
      })
    );
  }
  private setCartByType(cart: Cart): void {
    this.cart = cart;
    if (!this.cartType && !this.authService.user.supportsYbo) {
      this.cartType = CartTypes.SelfService;
    }
    if (!cart.events?.length && this.authService.user.supportsYbo) {
      if (this.cartType) {
        this.cartType = null;
        this.storageService.remove(CartService.STORAGE_CART_TYPE);
      } else {
        const selfServiceCart: Observable<Cart> = this._selfServiceApi.selfServiceCartGet();
        const legacyCart: Observable<Cart> = this._api.getCart();
        forkJoin([selfServiceCart, legacyCart]).pipe(
          tap({error: (e) => this.snackService.showBackendError(e)})
        ).subscribe((results: Cart[]) => {
          if (results[0].events?.length) {
            this.cart = results[0];
            this.cartType = CartTypes.SelfService;
          } else if (results[1].events?.length) {
            this.cart = results[1];
            this.cartType = CartTypes.Legacy;
          }
          this.storageService.set(CartService.STORAGE_CART_TYPE, this.cartType);
        });
      }
    }
  }

  private setCartTimer(): void {
    this.timeRemaining = this.cart?.timeRemaining;
    if (!this.isEmpty) {
      clearInterval(this._expiryInterval);
      this._expiryInterval = setInterval(() => {
        this.timeRemaining = this.timeRemaining - 1;
        if (!this.timeRemaining || this.timeRemaining <= 0) {
          this.emptyCart(() => {
            this.snackService.showSuccess(this.translateService.instant('general.api_cart_timer_clear_success'));
          });
          this.timeRemaining = null;
          clearInterval(this._expiryInterval);
        }
        this.viewService.triggerCdr();
      }, 1000);
    } else {
      this.timeRemaining = null;
      clearInterval(this._expiryInterval);
    }
    this.viewService.triggerCdr();
  }

  public validateCart(ticketIds: TicketIds): Observable<Response> {
    return this._api.cartValidatePost(ticketIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public clearCart(): Observable<Cart> {
    return this._api.clear().pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public clearSelfServiceCart(): Observable<Cart> {
    return this._selfServiceApi.selfServiceCartDelete().pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public calculateMaxTickets(max: number): number {
    if (this.isSelfService) {
      const maxTicketsLeft = MAX_TICKETS_IN_CART - (this.cart?.total?.ticketQuantity || 0);
      if (max > maxTicketsLeft) {
        max = maxTicketsLeft;
      }
    }
    return max;
  }

  public override updateCurrentBooking(newBooking: BookingItemLight): void {
    super.updateCurrentBooking(newBooking);
    this.storageService.set(CartService.STORAGE_CART_LAST_BOOKING, JSON.stringify(newBooking));
  }

  public subTotals(ticketIds: TicketIds): Observable<CartTotal> {
    return this._api.calculateSubTotals(ticketIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public checkIsOwner(eventIds: number[]): Observable<Right> {
    return this._api.isowner(eventIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public checkIsOwnerAlias(eventIds: number[]): Observable<Right> {
    return this._api.isowneralias(eventIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public clearGiftTickets(): void {
    this.isLoading = true;
    this.removeGiftCodes({codes: this.giftCodes}).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((cart) => this._cart = cart);
  }

  public addGiftCodes(ticketBarcodes: TicketBarcodes): Observable<Cart> {
    return this._api.cartGiftPost(ticketBarcodes).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removeGiftCodes(ticketBarcodes: TicketBarcodes): Observable<Cart> {
    return this._api.cartGiftPut(ticketBarcodes).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public addSelfServiceGiftCode(giftCode: string): Observable<Cart> {
    return this._api.selfServiceCartGiftCardPost({code: giftCode}).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removeSelfServiceGiftCode(giftCode: string): Observable<Cart> {
    return this._api.selfServiceCartGiftCardPut({code: giftCode}).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public applyDiscount(discountValue: number): void {
    this.isLoading = true;
    this.updateDiscount({discountPercent: discountValue || 0}).pipe(
      tap({error: () => this.retryCart()}),
      finalize(() => this.isLoading = false)
    ).subscribe((cart) => this.updateCart(cart));
  }
  public updateDiscount(discount: Discount): Observable<Cart> {
    return this._api.cartDiscountPost(discount).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public addPromocode(promocodeRequest: PromocodeRequest): Observable<Cart> {
    return this._api.cartPromocodePost(promocodeRequest).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removePromocode(promocodeRequest: PromocodeRequest): Observable<Cart> {
    return this._api.cartPromocodePut(promocodeRequest).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public addSelfServicePromocode(promocodeRequest: PromocodeRequest, eventId: number): Observable<EventPricing> {
    return this._selfServiceApi.selfServiceEventIdPromoCodePost(eventId, promocodeRequest).pipe(
      tap({
        error: (e) => this.snackService.showBackendError(e),
        next: (eventPricing: EventPricing) => this.eventPricing = eventPricing
      })
    );
  }
  public removeSelfServicePromocode(eventId: number): Observable<EventPricing> {
    return this._selfServiceApi.selfServiceEventIdPromoCodePut(eventId).pipe(
      tap({
        error: (e) => this.snackService.showBackendError(e),
        next: (eventPricing: EventPricing) => this.eventPricing = eventPricing
      })
    );
  }

  public updateCart(newData: Cart, selectAllTickets: boolean = true): void {
    this.cart = newData;
    this.storageService.set(CartService.STORAGE_CART_UPDATE, moment.now());
    if (selectAllTickets) {
      this.updateTickets();
    }
  }

  public retryCart(selectTickets = false, eventId?: number): void {
    this.isLoading = true;
    this.getCart().pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(() => {
      if (selectTickets) {
        this.selectAllTickets();
      }
      if (eventId) {
        this.updateSelectedTicketsTotal(eventId);
      }
    });
  }

  public emptyCart(callback?: Function): void {
    if (this.isSelfService || !this.authService.user.supportsYbo) {
      this.clearSelfServiceCart().subscribe((cart) => this.finishClearCart(cart, false, callback));
    } else {
      this.emptyLegacyCart(callback);
    }
  }
  public emptyLegacyCart(callback?: Function): void {
    this.clearCart().subscribe((cart) => this.finishClearCart(cart, true, callback));
  }
  private finishClearCart(cart: Cart, legacyOnly?: boolean, callback?: Function): void {
    this.storageService.set(CartService.STORAGE_CART_UPDATE, moment.now());
    this.storageService.set(CartService.STORAGE_CART_CLEAR, moment.now());
    this.updateClientNotificationEmail();
    this.preserveGiftTickets = false;
    this.cart = cart;
    this.ticketCount = 0;
    this.updateSelectedTicketsTotal();
    if (this._selectedEvent?.boEvent && !legacyOnly) {
      this.selfServiceEventService.getEventPricing(this._selectedEvent.id).subscribe((pricing) => this.eventPricing = pricing);
    }
    if (callback) {
      callback();
    }
  }

  public updateSubTotals(preserveGifts: boolean = false): void {
    if (preserveGifts) {
      this.storageService.set(CartService.STORAGE_CART_PRESERVE_GIFTS, moment.now());
    }
    this.isLoading = true;
    if (this.hasSelectedTickets) {
      this.subTotals({tickets: this.getSelectedTicketIds}).pipe(
        tap({error: () => this.retryCart()}),
        finalize(() => this.isLoading = false)
      ).subscribe((cartTotal) => this.finishSubTotals(cartTotal));
    } else {
      this.finishSubTotals(cloneDeep(this._emptyTotals));
    }
  }
  private finishSubTotals(total: CartTotal): void {
    this.setSubTotals(total);
    this.storageService.set(CartService.STORAGE_CART_UPDATE_SUBTOTALS, JSON.stringify(this.selectionMap));
    this.isLoading = false;
  }

  public getPaymentTypes(): Observable<PaymentTypeEnum[]> {
    return this._api.paymentTypesGet().pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public updateCartForEvent(inEventId: number): void {
    this.updateSelectedTicketsTotal(inEventId);
  }
  public updateSelectedTicketsTotal(inEventId?: number): void {
    this.isLoading = true;
    if (!inEventId) {
      inEventId = Number(this.storageService.get(EventPurchaseComponent.STORAGE_EVENT_PURCHASE_ID)) || null;
    }
    this._selectedEvent = null;
    if (inEventId) {
      this._selectedEvent = {
        id: inEventId,
        boEvent: JSON.parse(this.storageService.get(EventPurchaseComponent.STORAGE_EVENT_IS_BO)) || null
      }
    }
    const selectedEventInCart: CartSortedEvent = this.ticketEventMap.find(e => e.id === inEventId);
    if (selectedEventInCart) {
      if (selectedEventInCart.quantity === this.ticketCount) {
        this.selectedTicketsTotal = this.cartTotal;
        this.isLoading = false;
      } else {
        if (selectedEventInCart.boEvent) {
          const newCartTotal: CartTotal = cloneDeep(this.cartTotal);
          newCartTotal.ticketQuantity = selectedEventInCart.quantity;
          newCartTotal.ticketTotal = selectedEventInCart.total;
          newCartTotal.surcharges = selectedEventInCart.surcharges;
          newCartTotal.total = selectedEventInCart.total;
          selectedEventInCart.surcharges.forEach(surcharge => newCartTotal.total = newCartTotal.total + surcharge.total);
          this.selectedTicketsTotal = newCartTotal;
          this.isLoading = false;
        } else {
          this.subTotals({tickets: this.getEventTicketIds(selectedEventInCart.id)}).pipe(
            finalize(() => this.isLoading = false)
          ).subscribe((cartTotal) => this.selectedTicketsTotal = cartTotal);
        }
      }
    } else {
      this.clearSelectedTicketsTotal();
      this.isLoading = false;
    }
  }
  public toggleSelectedTickets(): void {
    this.showSelectedTickets = !this.showSelectedTickets;
  }
  public get selectedTicketsTotal(): CartTotal {
    return this._selectedTicketsTotal;
  }
  public set selectedTicketsTotal(total: CartTotal) {
    this._selectedTicketsTotal = total;
    this.selectedTicketsMap.event = this.ticketEventMap.find(e => e.id === this._selectedEvent?.id);
    this.selectedTicketsMap.selectedTickets = this.selectedTicketsMap.event?.legacyTickets;
    this.selectedTicketsMap.selectedTicketTypes = this.selectedTicketsMap.event?.unassignedTickets;
    if (!this.selectedTicketsMap.selectedTickets?.length && !this.selectedTicketsMap.selectedTicketTypes?.length) {
      this.showSelectedTickets = false;
    }
  }
  private clearSelectedTicketsTotal(): void {
    this.selectedTicketsTotal = cloneDeep(this._emptyTotals);
  }

  public override updateTicketCustomPrice(eventId: number, ticket: CustomTicket): void {
    this.isLoading = true;
    super.updateTicketCustomPrice(eventId, ticket);
    this.updateTicketPrice(eventId, this.customPriceRequest).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe();
  }
  public updateTicketPrice(eventId: number, tickets: TicketPrice[]): Observable<Cart> {
    return this._api.updateTicketPrice(tickets).pipe(
      tap({
        error: (e) => {
          this.snackService.showBackendError(e);
          this.retryCart();
        },
        next: (cart: Cart) => {
          this.updateCart(cart, false);
          this.updateCartForEvent(eventId);
        }
      })
    );
  }

  public override updateTicketPriceType(eventId: number, ticket: LegacyTicket): void {
    this.isLoading = true;
    super.updateTicketPriceType(eventId, ticket);
    if (this.priceTypeRequest?.length) {
      this.updateTicketPriceTypes(eventId, this.priceTypeRequest).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe();
    }
  }
  public updateTicketPriceTypes(eventId: number, tickets: TicketPriceType[]): Observable<Cart> {
    return this._api.updateTicketPriceType(tickets).pipe(
      tap({
        error: (e) => {
          this.snackService.showBackendError(e);
          this.retryCart();
        },
        next: (cart: Cart) => {
          this.updateCart(cart);
          this.updateCartForEvent(eventId);
        }
      })
    );
  }

  public addTickets(eventTicketIds: EventTicketIds): Observable<Cart> {
    return this._api.addTicketsToCart(eventTicketIds).pipe(
      tap({
        error: (e) => this.snackService.showBackendError(e),
        next: (cart) => this.updateCartWithSubTotals(cart)
      })
    );
  }
  public addPlaces(places: PlacesRequest): Observable<Cart> {
    return this._api.addPlacesToCart(places).pipe(
      tap({
        error: (e) => this.snackService.showBackendError(e),
        next: (cart) => this.updateCartWithSubTotals(cart)
      })
    );
  }
  public removeTickets(ticketIds: number[] = this.getSelectedTicketIds): void {
    this.isLoading = true;
    this.removeCartTickets({tickets: ticketIds}).pipe(
      tap({error: () => this.retryCart()}),
      finalize(() => this.isLoading = false)
    ).subscribe();
  }
  public removeCartTickets(ticketIds: TicketIds): Observable<Cart> {
    return this._api.removeTicketsFromCart(ticketIds).pipe(
      tap({
        error: (e) => this.snackService.showBackendError(e),
        next: (cart) => {
          this.updateCartWithSubTotals(cart);
          this.storageService.set(CartService.STORAGE_CART_UPDATE, moment.now());
        }
      })
    );
  }
  private updateCartWithSubTotals(cart: Cart): void {
    this.cart = cart;
    this.updateSelectionMap(!!cart?.events?.length);
  }

  public removeTicketTypes(eventId: number, ticketsRequests: TicketsRequest[]): void {
    this.isLoading = true;
    this.setUnassignedTickets(eventId, ticketsRequests).pipe(
      tap({error: () => this.retryCart()}),
      finalize(() => this.isLoading = false)
    ).subscribe((response) => {
      this.updateCart(response.cart, false);
      if (eventId === this._selectedEvent?.id) {
        this.eventPricing = response.pricing;
        this.updateCartForEvent(eventId);
      }
    });
  }
  public setUnassignedTickets(eventId: number, ticketsRequests: TicketsRequest[]): Observable<TicketsResponse> {
    return this._api.selfServiceEventIdTicketsPut(eventId, ticketsRequests).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public saveTransaction(checkout: Checkout): Observable<Transaction> {
    return this._api.saveTransaction(checkout).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public sendTransaction(transactionId: number, sendType: SendType, priceInfo: PriceInfoEnum, ticketIds?: Array<number>, sendTransaction?: SendTransaction): Observable<Response> {
    return this._api.sendTransaction(transactionId, sendType, priceInfo, ticketIds, sendTransaction).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public selfServiceCheckout(checkoutRequest: CheckoutRequest): Observable<Transaction> {
    return this._selfServiceApi.selfServiceCartCheckoutPost(checkoutRequest).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public printTransaction(transactionId: number, ticketType: PrintTicketType, priceInfo: PriceInfoEnum, selectedTicketIds?: number[]): Observable<Blob> {
    return this._api.printTransaction(transactionId, ticketType, priceInfo, selectedTicketIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public printSelfServiceTransaction(transactionId: number): Observable<Blob> {
    return this._selfServiceApi.getTransactionTicketsPdfCombined(transactionId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public printFiscal(transactionId: number): Observable<Blob> {
    return this._api.printFiscalGet(transactionId, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public clearUserCart(): void {
    this.clearBooking();
    this._selectedEvent = null;
    this.storageService.remove(EventPurchaseComponent.STORAGE_EVENT_PURCHASE_ID);
    this.storageService.remove(EventPurchaseComponent.STORAGE_EVENT_IS_BO);
    this.finishClearCart(this._emptyCart, false,() => clearInterval(this._expiryInterval));
  }
}
